import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { TOOLS, TOOL_TIERS, TIER_BADGE, canAccessTool } from "../../data/tools";
import { useAuth } from "../../lib/AuthContext";
import { useLang } from "../../i18n/LanguageContext";
import { scrollToId } from "../../hooks/useLenis";
import { ToolConsole } from "./ToolConsole";
import { UpgradeModal } from "./UpgradeModal";
import { AibaLogo } from "./Logo";
import {
  PenTool, MessageSquare, AudioLines, TrendingUp, Dna,
  Clapperboard, Radar, Cog, Coins, Play, Video, Lock,
} from "lucide-react";

const ICONS = { PenTool, MessageSquare, AudioLines, TrendingUp, Dna, Clapperboard, Radar, Cog, Video };
const COST = { text: 5, image: 20, voice: 15 };

const ToolCell = ({ tool, index, onOpen, locked, onLocked }) => {
  const Icon = ICONS[tool.icon];
  const master = tool.master;
  const cost = tool.kind === "video" ? "30+" : COST[tool.kind];

  return (
    <motion.div
      data-testid={`cockpit-cell-${tool.id}`}
      initial={{ opacity: 0, scale: 0.94, y: 24 }}
      whileInView={{ opacity: 1, scale: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: index * 0.05 }}
      className={`group relative flex aspect-square flex-col justify-between overflow-hidden rounded-2xl border p-6 backdrop-blur-xl transition-[border-color,background-color,box-shadow] duration-500 ${
        master ? "border-blue-400/50 bg-blue-400/[0.06] glow-cyan" : "border-blue-500/15 bg-[#0F172A]/40 hover:border-blue-400/40 hover:bg-[#08131f]/60"
      }`}
    >
      {master && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="absolute h-24 w-24 rounded-full border border-blue-400/40" style={{ animation: "pulse-ring 2.4s ease-out infinite" }} />
          <span className="absolute h-24 w-24 rounded-full border border-blue-400/30" style={{ animation: "pulse-ring 2.4s ease-out infinite", animationDelay: "1.2s" }} />
        </div>
      )}

      <div className="relative z-10 flex items-start justify-between">
        <div className={`flex h-11 w-11 items-center justify-center rounded-xl border transition-colors duration-500 ${master ? "border-blue-400/50 bg-blue-400/10" : "border-blue-500/20 bg-[#0F172A]/60 group-hover:border-blue-400/40"}`}>
          {master ? <AibaLogo size={22} /> : Icon ? <Icon size={19} className="text-blue-300" /> : null}
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {TOOL_TIERS[tool.id] && (
            <span data-testid={`cockpit-tier-${tool.id}`} className={`flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.15em] ${TIER_BADGE[TOOL_TIERS[tool.id]]}`}>
              {locked && <Lock size={8} />}{TOOL_TIERS[tool.id]}
            </span>
          )}
          <div className="flex items-center gap-1.5 rounded-full border border-blue-500/20 bg-[#0F172A]/50 px-2.5 py-1" data-testid={`cockpit-cost-${tool.id}`}>
            <Coins size={11} className="text-blue-300" />
            <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-blue-200">{cost} cr</span>
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <h3 className={`font-display text-lg font-700 tracking-tighter ${master ? "text-blue-100 text-glow" : "text-white"}`}>{tool.name}</h3>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#8AA0C0]">{tool.tagline}</p>
        {locked ? (
          <button
            data-testid={`cockpit-unlock-${tool.id}`}
            onClick={() => onLocked(tool)}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-amber-200 transition-all duration-300 hover:bg-amber-400/20"
          >
            <Lock size={12} /> Débloquer · {TOOL_TIERS[tool.id]}
          </button>
        ) : (
          <button
            data-testid={`cockpit-open-${tool.id}`}
            onClick={() => onOpen(tool)}
            className={`mt-5 flex w-full items-center justify-center gap-2 rounded-full border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] transition-all duration-300 ${
              master ? "border-blue-400 bg-blue-500 text-white cta-glow" : "border-blue-400/30 bg-blue-400/5 text-blue-100 hover:bg-blue-500 hover:text-white"
            }`}
          >
            <Play size={12} /> Ouvrir la console
          </button>
        )}
      </div>
    </motion.div>
  );
};

export const Cockpit = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { user, setAuthOpen } = useAuth();
  const { t } = useLang();
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [remixPrompt, setRemixPrompt] = useState("");
  const [upgrade, setUpgrade] = useState({ open: false, tool: null, tier: null });

  const openConsole = (tool) => {
    setRemixPrompt("");
    setSelected(tool);
    setOpen(true);
  };

  // Remix: /?remix=<prompt> opens the AIBA Video console prefilled with the purchased prompt.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const remix = params.get("remix");
    if (remix) {
      const videoTool = TOOLS.find((tl) => tl.kind === "video");
      if (videoTool) {
        setRemixPrompt(remix);
        setSelected(videoTool);
        setOpen(true);
        setTimeout(() => scrollToId("cockpit"), 300);
      }
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const onLocked = (tool) => {
    if (!user) {
      // Force login first for anonymous visitors
      setAuthOpen(true);
      return;
    }
    setUpgrade({ open: true, tool, tier: TOOL_TIERS[tool.id] });
  };

  return (
    <section id="cockpit" data-testid="cockpit-section" className="relative py-28 lg:py-36">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(37,99,235,0.06),transparent_60%)]" />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div ref={ref} className="mb-14 flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
          <div className="max-w-2xl">
            <motion.span initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} className="font-mono text-xs uppercase tracking-[0.35em] text-blue-400">
              [ {t("cockpit.tag")} ]
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mt-4 font-display text-4xl font-800 tracking-tighter text-white sm:text-5xl lg:text-6xl"
            >
              {t("cockpit.title")}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="mt-5 font-body text-base leading-relaxed text-[#8AA0C0] sm:text-lg"
            >
              {t("cockpit.subtitle")}
            </motion.p>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-400/5 px-4 py-2">
            <Coins size={14} className="text-blue-300" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-blue-200" data-testid="cockpit-balance">
              {user ? `${user.credits} crédits` : "Connectez-vous"}
            </span>
          </div>
        </div>

        <div className="mx-auto grid max-w-4xl grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3">
          {TOOLS.map((tool, i) => (
            <ToolCell key={tool.id} tool={tool} index={i} onOpen={openConsole} locked={!canAccessTool(user, tool.id)} onLocked={onLocked} />
          ))}
        </div>
      </div>

      <ToolConsole tool={selected} open={open} onClose={() => setOpen(false)} initialPrompt={remixPrompt} />
      <UpgradeModal open={upgrade.open} tool={upgrade.tool} tier={upgrade.tier} onClose={() => setUpgrade({ open: false, tool: null, tier: null })} />
    </section>
  );
};
